import {
  AnchoredPanel,
  AnchoredPanelType,
  getResourceLifecycleStatus,
  LabelMetaItem,
  MetaItemGroupsLayout,
  MetaItemsGroupsDirection,
  stateT,
  Status,
} from "o4a-react";
import * as React from "react";
import { Spinner, Stack } from "@fluentui/react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { AzureResourceGroupLink } from "../../components/AzureLinks/AzureResourceGroupLink";
import { AzureSubscriptionLink } from "../../components/AzureLinks/AzureSubscriptionLink";
import { metaItemLabelsGap, ttlOneMinCaching } from "../../constants/uiConstants";
import { parseId } from "../../helpers/idHelper";
import { getStatusInfo, optimizedRetryOption } from "../../helpers/resourceHelper";
import { useQueryCall } from "../../hooks/useQueryCall";
import { AzureSubscriptionSummaryExt } from "../../hooks/useSubscriptions";
import { getAzureLocationName, getOciRegion } from "../../utils";
import { OperationActionProps } from "../OperationTypes";

export interface ExaVmClusterViewDbNodeProps extends OperationActionProps {
    dbNodeId: string | undefined;
    subscriptions: AzureSubscriptionSummaryExt[];
  }

export const ExaVmClusterViewDbNode = ({
  targetId,
  dbNodeId,
  subscriptions,
  location,
  onCancel,
}: ExaVmClusterViewDbNodeProps): JSX.Element => {
  const idComps = parseId(dbNodeId);
  const resGroup = decodeURIComponent(idComps?.resourceGroup || "");
  const resourceName = decodeURIComponent(idComps?.resourceName || "");
  const subscription = subscriptions?.find(item => item.id === idComps?.subscriptionId);
  const { response, loading } = useQueryCall({
    wait: !dbNodeId,
    method: apiClients.withRegion(getOciRegion(location)).exaDatabaseApi.getDbNode,
    options: {
      args: {
        subscriptionId: idComps?.subscriptionId,
        resourceGroupName: resGroup,
        dbNodeName: resourceName,
      },
      caching: ttlOneMinCaching,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadExadbNode(),
      },
    },
  });
    
  const dbNode = response?.data;

  const essentialsMetaItemGroup: JSX.Element[] = [
    <Stack tokens={{ childrenGap: metaItemLabelsGap }}>
      <LabelMetaItem label={Messages.labels.resourceGroup()}>
        <AzureResourceGroupLink resourceId={dbNodeId as string} />
      </LabelMetaItem>
      <LabelMetaItem label={Messages.labels.status()}>
        <Status
          iconPosition="right"
          label={stateT(dbNode?.status || "")}
          status={getResourceLifecycleStatus(dbNode?.status || "")}
          statusInfo={getStatusInfo(
            dbNode?.status || "",
            dbNode?.lastOperationStatus || "",
            dbNode?.lastOperationStatusDetails || "",
          )}
        />
      </LabelMetaItem>
      <LabelMetaItem label={Messages.labels.location()}>
        {getAzureLocationName(dbNode?.location || "")}
      </LabelMetaItem>
      <LabelMetaItem label={Messages.labels.subscription()}>
        <AzureSubscriptionLink resourceId={dbNodeId as string} subscriptionName={subscription?.name} />
      </LabelMetaItem>
      <LabelMetaItem label={Messages.labels.subscriptionId()}>
        {idComps?.subscriptionId}
      </LabelMetaItem>
    </Stack>,
    <Stack tokens={{ childrenGap: metaItemLabelsGap }}>
      <LabelMetaItem label={Messages.labels.coreCount()}>
        {dbNode?.cpuCoreCount?.toString() || ""}
      </LabelMetaItem>
      <LabelMetaItem label={Messages.labels.memoryInGB()}>
        {dbNode?.memorySizeInGBs?.toString() || ""}
      </LabelMetaItem>
      <LabelMetaItem label={Messages.labels.hostNameOnly()}>
        {dbNode?.hostname || ""}
      </LabelMetaItem>
      <LabelMetaItem label={Messages.labels.hostIp()}>
        {dbNode?.hostIp || ""}
      </LabelMetaItem>
    </Stack>,
  ];
    
  return (
    <AnchoredPanel
      anchorId={targetId}
      type={AnchoredPanelType.CUSTOM_WIDTH}
      customWidth={700}
      title={dbNode?.name || ""}
      isOpen
      onClose={onCancel}
    >
      {loading ? <Spinner label={Messages.common.loading()} />
        : (
          <MetaItemGroupsLayout direction={MetaItemsGroupsDirection.Vertical}>
            { essentialsMetaItemGroup }
          </MetaItemGroupsLayout>
        )}
    </AnchoredPanel>
  );
};

export const newExaVmClusterViewDbNode = (
  props: ExaVmClusterViewDbNodeProps,
): JSX.Element => (<ExaVmClusterViewDbNode {...props} />);
